import React, { useState, ReactElement } from 'react';
import { Box, Container, Typography, Paper, Divider, FormControlLabel, Switch } from '@mui/material';
import AdComponent from '../components/AdComponent';

// Test ad code examples for different positions
const TEST_ADS = {
  ABOVE_TITLE: `<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"></script>
<!-- Test Ad Above Title -->
<ins class="adsbygoogle"
     style="display:block"
     data-ad-client="ca-pub-xxxxxxxxxxxxxxxx"
     data-ad-slot="test-above"
     data-ad-format="auto"></ins>
<script>
     (adsbygoogle = window.adsbygoogle || []).push({});
</script>`,

  BELOW_TITLE: `<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"></script>
<!-- Test Ad Below Title -->
<ins class="adsbygoogle"
     style="display:block"
     data-ad-client="ca-pub-xxxxxxxxxxxxxxxx"
     data-ad-slot="test-below"
     data-ad-format="auto"></ins>
<script>
     (adsbygoogle = window.adsbygoogle || []).push({});
</script>`,

  MIDDLE_CONTENT: `<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"></script>
<!-- Test Ad Middle -->
<ins class="adsbygoogle"
     style="display:block"
     data-ad-client="ca-pub-xxxxxxxxxxxxxxxx"
     data-ad-slot="test-middle"
     data-ad-format="auto"></ins>
<script>
     (adsbygoogle = window.adsbygoogle || []).push({});
</script>`,

  AFTER_CONTENT: `<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"></script>
<!-- Test Ad After Content -->
<ins class="adsbygoogle"
     style="display:block"
     data-ad-client="ca-pub-xxxxxxxxxxxxxxxx"
     data-ad-slot="test-after"
     data-ad-format="auto"></ins>
<script>
     (adsbygoogle = window.adsbygoogle || []).push({});
</script>`,
};

// Sample content paragraphs
const SAMPLE_PARAGRAPHS = [
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
  'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
  'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
  'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
  'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium.'
];

const AdTestPage: React.FC = () => {
  const [showBorders, setShowBorders] = useState(true);
  const [showAboveAd, setShowAboveAd] = useState(true);
  const [showBelowAd, setShowBelowAd] = useState(true);
  const [showMiddleAd, setShowMiddleAd] = useState(true);
  const [showAfterAd, setShowAfterAd] = useState(true);

  // Split content and insert ads
  const renderContentWithAds = (): ReactElement[] => {
    const result: ReactElement[] = [];
    
    // Create paragraphs with ads in between
    SAMPLE_PARAGRAPHS.forEach((paragraph, index) => {
      // Add paragraph
      result.push(
        <Typography key={`p-${index}`} paragraph>
          {paragraph}
        </Typography>
      );
      
      // Add middle ad after every other paragraph, except the last one
      if (showMiddleAd && index % 2 === 1 && index < SAMPLE_PARAGRAPHS.length - 1) {
        result.push(
          <AdComponent 
            key={`ad-${index}`}
            adCode={TEST_ADS.MIDDLE_CONTENT}
            showBorders={showBorders}
          />
        );
      }
    });
    
    return result;
  };

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Google Ads Test Sayfası
        </Typography>
        
        <Typography paragraph>
          Bu sayfa Google Ads entegrasyonunu test etmek için tasarlanmıştır. 
          Reklamların farklı pozisyonlarda nasıl göründüğünü kontrol edebilirsiniz.
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={showBorders}
                onChange={(e) => setShowBorders(e.target.checked)}
              />
            }
            label="Reklam Sınırlarını Göster"
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={showAboveAd}
                onChange={(e) => setShowAboveAd(e.target.checked)}
              />
            }
            label="Başlık Üstü Reklam"
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={showBelowAd}
                onChange={(e) => setShowBelowAd(e.target.checked)}
              />
            }
            label="Başlık Altı Reklam"
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={showMiddleAd}
                onChange={(e) => setShowMiddleAd(e.target.checked)}
              />
            }
            label="İçerik Ortası Reklam"
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={showAfterAd}
                onChange={(e) => setShowAfterAd(e.target.checked)}
              />
            }
            label="İçerik Sonu Reklam"
          />
        </Box>
      </Paper>

      <Box sx={{ mb: 6 }}>
        {/* Above Title Ad */}
        {showAboveAd && (
          <AdComponent adCode={TEST_ADS.ABOVE_TITLE} showBorders={showBorders} />
        )}
        
        {/* Article Title */}
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Google Ads Test Başlığı
        </Typography>
        
        {/* Below Title Ad */}
        {showBelowAd && (
          <AdComponent adCode={TEST_ADS.BELOW_TITLE} showBorders={showBorders} />
        )}
        
        {/* Article Meta */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Yazar: Test Kullanıcı
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Tarih: {new Date().toLocaleDateString()}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Görüntülenme: 100
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Article Content with Ads */}
        <Box sx={{ mb: 4 }}>
          {renderContentWithAds()}
        </Box>
        
        {/* After Content Ad */}
        {showAfterAd && (
          <AdComponent adCode={TEST_ADS.AFTER_CONTENT} showBorders={showBorders} />
        )}
      </Box>
    </Container>
  );
};

export default AdTestPage; 